// File: src/components/FinanceSummary.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

function FinanceSummary() {
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [accountSummary, setAccountSummary] = useState([]); // 👈 new state
  const [error, setError] = useState("");

  const [profit, setProfit] = useState("");
  const [profitOther, setProfitOther] = useState("");
  const [profitScreen, setProfitScreen] = useState("");
  const [profitEthio, setProfitEthio] = useState("");

  const [unreceivedPhones, setUnreceivedPhones] = useState("");
  const [unreceivedOther, setUnreceivedOther] = useState("");
  const [unreceivedScreen, setUnreceivedScreen] = useState("");

  const [unpaidPhones, setUnpaidPhones] = useState("");
  const [unpaidOther, setUnpaidOther] = useState("");
  const [unpaidScreen, setUnpaidScreen] = useState("");
  const [unpaidEthio, setUnpaidEthio] = useState("");

  const [total, setTotal] = useState("");
  const [totalUnreceived, setTotalUnreceived] = useState("");
  const [totalUnpaid, setTotalUnpaid] = useState("");
  const [totalEthio, setTotalEthio] = useState("");
  const [grandTotal, setGrandTotal] = useState("");

  const [remainingPhones, setRemainingPhones] = useState("");
  const [remainingOther, setRemainingOther] = useState("");
  const [remainingScreen, setRemainingScreen] = useState("");
  const [remainingEthio, setRemainingEthio] = useState("");
  const [totalRemaining, setTotalRemaining] = useState("");

  const [revenue, setRevenue] = useState("");

  const [loading, setLoading] = useState(false);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const currentMonthShortForm = () => {
    const monthIndex = new Date().getMonth();
    return months[monthIndex];
  };

  const [selectedMonth, setSelectedMonth] = useState(currentMonthShortForm());

  const fetchData = useCallback(async () => {
    setSelectedMonth(currentMonthShortForm());
    try {
      setLoading(true);

      const financeRes = await axios.get(
        `${apiUrl}/itemPrice/${selectedMonth}`
      );
      setData(financeRes.data);
      //account summary
      const accountsRes = await axios.get(`${apiUrl}/accounts/summary`);
      setAccountSummary(accountsRes.data.accounts || []);
      setGrandTotal(accountsRes.data.grandTotal || 0);

      // profits
      const [phoneRes, otherRes, screenRes, ethioRes] = await Promise.all([
        axios.get(`${apiUrl}/finance/profitAll/phone`),
        axios.get(`${apiUrl}/finance/profitAll/other`),
        axios.get(`${apiUrl}/finance/profitAll/screen`),
        axios.get(`${apiUrl}/finance/profitAll/ethio`),
      ]);

      // Set individual profits
      setProfit(phoneRes.data.profit);
      setProfitOther(otherRes.data.profit);
      setProfitScreen(screenRes.data.profit);
      setProfitEthio(ethioRes.data.profit);

      // Calculate total
      const total =
        (phoneRes.data.profit || 0) +
        (otherRes.data.profit || 0) +
        (screenRes.data.profit || 0) +
        (ethioRes.data.profit || 0);
      setTotal(total);

      //Fetch unreceived
      const unreceivedRes = await axios.get(
        `${apiUrl}/finance/unreceivedAll/${selectedMonth}`
      );

      setUnreceivedPhones(unreceivedRes.data.unreceivedPhones || 0);
      setUnreceivedOther(unreceivedRes.data.unreceivedOther || 0);
      setUnreceivedScreen(unreceivedRes.data.unreceivedScreen || 0);

      const totalUnreceivedSum =
        (unreceivedRes.data.unreceivedPhones || 0) +
        (unreceivedRes.data.unreceivedOther || 0) +
        (unreceivedRes.data.unreceivedScreen || 0);
      setTotalUnreceived(totalUnreceivedSum);

      //Fetch unpaid
      const unpaidRes = await axios.get(
        `${apiUrl}/finance/unPaidAll/${selectedMonth}`
      );

      setUnpaidPhones(unpaidRes.data.unpaidPhones);
      setUnpaidOther(unpaidRes.data.unpaidOther);
      setUnpaidScreen(unpaidRes.data.unpaidScreen);
      setUnpaidEthio(unpaidRes.data.unpaidEthio);

      const totalUnpaidSum =
        (unpaidRes.data.unpaidPhones || 0) +
        (unpaidRes.data.unpaidOther || 0) +
        (unpaidRes.data.unpaidScreen || 0) +
        (unpaidRes.data.unpaidEthio || 0);
      setTotalUnpaid(totalUnpaidSum);

      //Fetch remaining
      const remainingRes = await axios.get(
        `${apiUrl}/finance/remainingAll/${selectedMonth}`
      );

      setRemainingPhones(remainingRes.data.remainingPhones);
      setRemainingOther(remainingRes.data.remainingOther);
      setRemainingScreen(remainingRes.data.remainingScreen);
      setRemainingEthio(remainingRes.data.remainingEthio);

      const totalRemainingSum =
        (remainingRes.data.remainingPhones || 0) +
        (remainingRes.data.remainingOther || 0) +
        (remainingRes.data.remainingScreen || 0) +
        (remainingRes.data.remainingEthio || 0);
      setTotalRemaining(totalRemainingSum);
      const revenueSum =
        total +
        totalUnpaidSum +
        totalRemainingSum +
        totalUnreceivedSum +
        grandTotal;
      setRevenue(revenueSum);
      setLoading(false);
    } catch (err) {
      console.error("❌ Axios error:", err);
      setError(err.response?.data?.message || "Error fetching data");
      setLoading(false);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setSelectedMonth(currentMonthShortForm());
    try {
      setLoading(true);

      const expensesRes = await axios.get(`${apiUrl}/api/expenses`);
      setExpenses(expensesRes.data);
      setLoading(false);
    } catch (err) {
      console.error("❌ Axios error:", err);
      setError(err.response?.data?.message || "Error fetching data");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchExpenses();
  }, [fetchData]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (loading || !data) return <p>Loading...</p>;

  const {
    productPrice,
    otherProductPrice,
    screenPrice,
    totalPrice,
    sendMoney,
    carryOver,
    available,
    difference,
    status,
  } = data;

  return (
    <div className="flex flex-row space-x-5 w-3/4 mx-auto mt-10">
      {/* LEFT CARD */}
      <div className="p-4 border rounded space-y-2 bg-white shadow w-1/3">
        <h2 className="text-lg font-bold mb-2">
          Finance Summary After May 2025
        </h2>
        <h3 className="text-lg font-bold mb-2">
          Current month {selectedMonth}
        </h3>

        <p>
          🧾 <strong>Total Price:</strong> ${totalPrice.toLocaleString()}
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>Products: ${productPrice.toLocaleString()}</li>
          <li>Other Products: ${otherProductPrice.toLocaleString()}</li>
          <li>Screen: ${screenPrice.toLocaleString()}</li>
        </ul>

        <p>
          💸 <strong>Send Money:</strong> ${sendMoney.toLocaleString()}
        </p>
        <p>
          📦 <strong>Carry Over:</strong> ${carryOver.toLocaleString()}
        </p>
        <p>
          ✅ <strong>Available:</strong> ${available.toLocaleString()}
        </p>
        <p>
          📉 <strong>Difference:</strong> ${difference.toLocaleString()} —{" "}
          <span
            className={status === "debt" ? "text-red-600" : "text-green-600"}
          >
            {status === "debt" ? "Debt" : "Remaining"}
          </span>
        </p>
      </div>

      {/* MIDDLE CARD */}
      <div className="p-4 border rounded space-y-2 bg-white shadow w-1/3">
        <h2 className="text-lg font-bold mb-2">Finance summary using Birr</h2>
        <h3 className="text-lg font-bold mb-2">
          Current month {selectedMonth}
        </h3>
        <p>
          <strong>Profit: </strong>{" "}
          <strong className="underline italic">
            {" "}
            {total.toLocaleString()} Br
          </strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>
            Phone: <strong>{profit?.toLocaleString()} Br</strong>
          </li>
          <li>
            Other Products: <strong>{profitOther?.toLocaleString()} Br</strong>
          </li>
          <li>
            Screens: <strong>{profitScreen?.toLocaleString()} Br</strong>
          </li>
          <li>
            Local products: <strong>{profitEthio?.toLocaleString()} Br</strong>
          </li>
        </ul>

        {/* ----------------Remaining */}

        <p>
          <strong>Remaining Items:</strong>
          <strong className="underline italic">
            {" "}
            {totalRemaining.toLocaleString()} Br
          </strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>
            Phone: <strong>{remainingPhones.toLocaleString()} Br</strong>
          </li>
          <li>
            Other Products:{" "}
            <strong>{remainingOther.toLocaleString()} Br</strong>
          </li>
          <li>
            Screens: <strong> {remainingScreen.toLocaleString()}Br</strong>
          </li>
          <li>
            Local products:{" "}
            <strong>{remainingEthio.toLocaleString()} Br</strong>
          </li>
        </ul>

        {/* ------------------Unpaid */}

        <p>
          <strong>Unpaid:</strong>
          <strong className="underline italic">
            {totalUnpaid.toLocaleString()} Br
          </strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>
            Phone: <strong>{unpaidPhones.toLocaleString()} Br</strong>
          </li>
          <li>
            Other Products: <strong>{unpaidOther.toLocaleString()} Br</strong>
          </li>
          <li>
            Screens: <strong>{unpaidScreen.toLocaleString()} Br</strong>
          </li>
          <li>
            Local products: <strong>{unpaidEthio.toLocaleString()} Br</strong>
          </li>
        </ul>

        {/* ----------------- Unreceived */}

        <p>
          <strong>Unreceived:</strong>{" "}
          <strong className="underline italic">
            {totalUnreceived.toLocaleString()} Br
          </strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>
            Phone: <strong>{unreceivedPhones.toLocaleString()} Br</strong>
          </li>
          <li>
            Other Products:{" "}
            <strong>{unreceivedOther.toLocaleString()} Br</strong>
          </li>
          <li>
            Screens: <strong>{unreceivedScreen.toLocaleString()} Br</strong>
          </li>
        </ul>

        {/* ------------------Bank */}

        <p>
          <strong>Bank Accounts:</strong>
          <strong className="underline italic">
            {" "}
            {grandTotal.toLocaleString()} Br
          </strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          {accountSummary.map((acc) => (
            <li key={acc.account}>
              {acc.account}: <strong>{acc.total.toLocaleString()}</strong>
            </li>
          ))}
        </ul>
        <p>
          <strong>Revenue:</strong>
          <strong className="underline italic">
            {" "}
            {revenue.toLocaleString()} Br
          </strong>
        </p>
      </div>

      {/* RIGHT CARD */}
      <div className="p-4 border rounded space-y-2 bg-white shadow w-1/3">
        <h2 className="text-lg font-bold mb-2">Expenses</h2>
        <p>
          <strong>Office Rent:</strong> $23,500/month
        </p>
        <p>
          <strong>Internet:</strong> $1,800/month
        </p>
        <p>
          <strong>Server rent:</strong> $2,300/year
        </p>
        <p>
          <strong>Advertising:</strong>
        </p>
        <ul className="ml-10 list-disc text-sm">
          <li>jiji: $1,000/month</li>
          <li>afrotie: $1,200/month</li>
          <li>telegram: $700/month</li>
        </ul>
        <p>
          <strong>Salary: </strong>$15,000/month
        </p>
        <p>
          <strong>miscellaneous: </strong>
          {expenses
            ? `$${expenses
                .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
                .toLocaleString()}`
            : "$0"}
        </p>
        <ul className="max-h-40 overflow-y-auto">
          {expenses.map((e) => (
            <li key={e.id} className="flex justify-between items-center">
              <div>
                <p className="font-semibold">{e.description}</p>
                <p className="text-sm text-gray-500">{e.date}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-800">
                  ${parseFloat(e.amount).toFixed(2)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default FinanceSummary;
